import React, { useState, useEffect } from "react";
import { db, functions } from "../firebase";
import { collection, query, where, orderBy, onSnapshot, doc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

const COMMODITIES = ["XAUUSD", "XAGUSD", "USOIL", "XPTUSD", "NATGAS", "COPPER", "COCOA", "CORN"];

const fetchLivePrice = async (symbol, adminPrices) => {
  try {
    if (COMMODITIES.includes(symbol)) {
      const adminPrice = adminPrices[symbol]?.price;
      if (adminPrice > 0) return adminPrice;
      return 0;
    }

    const cryptoMap = {
      BTCUSD: "bitcoin", ETHUSD: "ethereum", ADAUSD: "cardano",
      BNBUSD: "binancecoin", SOLUSD: "solana"
    };
    const forexMap = {
      EURUSD: "EUR", GBPUSD: "GBP", USDJPY: "JPY",
      AUDUSD: "AUD", USDCAD: "CAD", USDCHF: "CHF"
    };
    const stockMap = {
      AAPL: 175, TSLA: 180, GOOGL: 160,
      AMZN: 185, MSFT: 415, NVDA: 850
    };

    if (cryptoMap[symbol]) {
      const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${cryptoMap[symbol]}&vs_currencies=usd`);
      const data = await res.json();
      return data[cryptoMap[symbol]]?.usd || 0;
    } else if (forexMap[symbol]) {
      const base = symbol.slice(0, 3);
      const quote = symbol.slice(3, 6);
      const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${base}`);
      const data = await res.json();
      return base === "USD" ? data.rates[quote] : data.rates["USD"];
    } else if (stockMap[symbol]) {
      return stockMap[symbol];
    }
    return 0;
  } catch (e) {
    return 0;
  }
};

const calcPnl = (trade, currentPrice) => {
  if (!trade.entryPrice || !currentPrice) return 0;
  const priceDiff = trade.direction === "buy"
    ? currentPrice - trade.entryPrice
    : trade.entryPrice - currentPrice;
  return parseFloat(((priceDiff / trade.entryPrice) * trade.amount * trade.leverage).toFixed(2));
};

export default function Trades({ user }) {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [closing, setClosing] = useState(null);
  const [msg, setMsg] = useState("");
  const [livePrices, setLivePrices] = useState({});
  const [adminPrices, setAdminPrices] = useState({});

  useEffect(() => {
    const q = query(
      collection(db, "trades"),
      where("userId", "==", user.uid),
      orderBy("openedAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setTrades(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [user]);

  // Listen to admin commodity prices in real-time
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "prices"), (snap) => {
      if (snap.exists()) {
        setAdminPrices(snap.data());
      }
    });
    return unsub;
  }, []);

  // Update live prices whenever admin prices change (instant for commodities)
  useEffect(() => {
    const openTrades = trades.filter(t => t.status === "open" && COMMODITIES.includes(t.asset));
    if (openTrades.length === 0) return;

    const newPrices = {};
    openTrades.forEach(t => {
      const adminPrice = adminPrices[t.asset]?.price;
      if (adminPrice > 0) newPrices[t.asset] = adminPrice;
    });
    if (Object.keys(newPrices).length > 0) {
      setLivePrices(prev => ({ ...prev, ...newPrices }));
    }
  }, [adminPrices, trades]);

  // Update non-commodity prices via API on interval
  useEffect(() => {
    const openTrades = trades.filter(t => t.status === "open" && !COMMODITIES.includes(t.asset));
    if (openTrades.length === 0) return;

    const updatePrices = async () => {
      const symbols = [...new Set(openTrades.map(t => t.asset))];
      const newPrices = {};
      await Promise.all(symbols.map(async (sym) => {
        const price = await fetchLivePrice(sym, adminPrices);
        if (price > 0) newPrices[sym] = price;
      }));
      setLivePrices(prev => ({ ...prev, ...newPrices }));
    };

    updatePrices();
    const interval = setInterval(updatePrices, 5000);
    return () => clearInterval(interval);
  }, [trades, adminPrices]);

  const handleClose = async (trade) => {
    setClosing(trade.id);
    setMsg("");
    try {
      const closePrice = livePrices[trade.asset] || trade.entryPrice;
      const closeTrade = httpsCallable(functions, "closeTrade");
      const result = await closeTrade({ tradeId: trade.id, closePrice });
      const pnl = result.data.pnl;
      setMsg(`✓ Trade closed! P&L: ${pnl >= 0 ? "+" : ""}$${pnl.toFixed(2)}`);
      setTimeout(() => setMsg(""), 4000);
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setClosing(null);
  };

  const filtered = filter === "all" ? trades : trades.filter(t => t.status === filter);
  const totalPnl = trades.filter(t => t.status === "closed").reduce((sum, t) => sum + (t.pnl || 0), 0);
  const openTrades = trades.filter(t => t.status === "open");
  const liveTotalPnl = openTrades.reduce((sum, t) => {
    const cp = livePrices[t.asset];
    return sum + (cp ? calcPnl(t, cp) : 0);
  }, 0);

  return (
    <div className="trades-page">
      <div className="trades-header">
        <div className="trades-title">My Trades</div>
        <div className="trades-filters">
          <div className={`filter-btn ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All ({trades.length})</div>
          <div className={`filter-btn ${filter === "open" ? "active" : ""}`} onClick={() => setFilter("open")}>Open ({openTrades.length})</div>
          <div className={`filter-btn ${filter === "closed" ? "active" : ""}`} onClick={() => setFilter("closed")}>Closed</div>
        </div>
      </div>

      <div className="trades-stats">
        <div className="trade-stat-card">
          <div className="trade-stat-label">Total Trades</div>
          <div className="trade-stat-val">{trades.length}</div>
        </div>
        <div className="trade-stat-card">
          <div className="trade-stat-label">Open Trades</div>
          <div className="trade-stat-val" style={{color:"#F0A500"}}>{openTrades.length}</div>
        </div>
        <div className="trade-stat-card">
          <div className="trade-stat-label">Closed P&L</div>
          <div className="trade-stat-val" style={{color: totalPnl >= 0 ? "#00C896" : "#FF3B5C"}}>
            {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)}
          </div>
        </div>
        <div className="trade-stat-card">
          <div className="trade-stat-label">Live P&L (Open)</div>
          <div className="trade-stat-val" style={{color: liveTotalPnl >= 0 ? "#00C896" : "#FF3B5C"}}>
            {liveTotalPnl >= 0 ? "+" : ""}${liveTotalPnl.toFixed(2)}
          </div>
        </div>
      </div>

      {msg && (
        <div className="dep-msg" style={{
          margin: "0 0 16px",
          color: msg.includes("✓") ? "#00C896" : "#FF3B5C",
          background: msg.includes("✓") ? "rgba(0,200,150,0.08)" : "rgba(255,59,92,0.08)"
        }}>{msg}</div>
      )}

      {loading ? (
        <div className="trades-empty">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="trades-empty">No trades found</div>
      ) : (
        <div className="trades-table-wrap">
          <table className="trades-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Direction</th>
                <th>Amount</th>
                <th>Leverage</th>
                <th>Entry Price</th>
                <th>Current Price</th>
                <th>Close Price</th>
                <th>Live P&L</th>
                <th>Final P&L</th>
                <th>Status</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const currentPrice = livePrices[t.asset];
                const livePnl = t.status === "open" && currentPrice ? calcPnl(t, currentPrice) : null;
                const decimals = t.entryPrice < 10 ? 4 : 2;
                return (
                  <tr key={t.id}>
                    <td className="td-asset">{t.asset}</td>
                    <td>
                      <span className={`dir-badge ${t.direction === "buy" ? "buy" : "sell"}`}>
                        {t.direction === "buy" ? "▲ BUY" : "▼ SELL"}
                      </span>
                    </td>
                    <td className="td-mono">${t.amount?.toFixed(2)}</td>
                    <td className="td-mono">x{t.leverage}</td>
                    <td className="td-mono">${t.entryPrice?.toFixed(decimals) || "-"}</td>
                    <td className="td-mono" style={{color:"#F0A500"}}>
                      {t.status === "open"
                        ? currentPrice ? `$${currentPrice.toFixed(decimals)}` : "..."
                        : "-"
                      }
                    </td>
                    <td className="td-mono">
                      {t.status === "closed" && t.closePrice > 0
                        ? `$${t.closePrice?.toFixed(decimals)}`
                        : "-"
                      }
                    </td>
                    <td className={`td-mono ${livePnl !== null ? livePnl >= 0 ? "td-green" : "td-red" : ""}`}>
                      {livePnl !== null
                        ? `${livePnl >= 0 ? "+" : ""}$${livePnl.toFixed(2)}`
                        : "-"
                      }
                    </td>
                    <td className={`td-mono ${t.status === "closed" ? t.pnl >= 0 ? "td-green" : "td-red" : ""}`}>
                      {t.status === "closed"
                        ? `${t.pnl >= 0 ? "+" : ""}$${t.pnl?.toFixed(2)}`
                        : "-"
                      }
                    </td>
                    <td>
                      <span className={`status-badge ${t.status === "open" ? "status-open" : "status-closed"}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="td-date">
                      {t.openedAt?.toDate ? t.openedAt.toDate().toLocaleDateString() : "-"}
                    </td>
                    <td>
                      {t.status === "open" && (
                        <button
                          className="btn-close-trade"
                          onClick={() => handleClose(t)}
                          disabled={closing === t.id}
                        >
                          {closing === t.id ? "..." : "Close"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}